import React, { useCallback, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { CourseDetailsFields } from "@/modules/learningCenter/features/CourseManagement/components/CourseDetailsFields/CourseDetailsFields";
import { CourseStructureNav } from "@/modules/learningCenter/features/CourseManagement/components/CourseStructureNav/CourseStructureNav";
import { CourseModulePane } from "@/modules/learningCenter/features/CourseManagement/components/CourseModulePane/CourseModulePane";
import { CourseEnrollmentStats } from "@/modules/learningCenter/features/CourseManagement/components/CourseEnrollmentStats/CourseEnrollmentStats";
import {
  CourseFormData,
  CourseFormModule,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import {
  Course,
  CourseMetadata,
} from "@/modules/learningCenter/features/CourseManagement/types/course";
import {
  AddFromLibraryModal,
  SelectedLibraryModule,
} from "@/modules/learningCenter/features/ModuleLibrary/components/AddFromLibraryModal/AddFromLibraryModal";
import { buildArchiveWarning } from "@/modules/learningCenter/features/CourseManagement/components/CourseList/archiveWarning";

interface CourseEditorLayoutProps {
  form: UseFormReturn<CourseFormData>;
  metadata: CourseMetadata | null;
  course: Course | null;
  loading?: boolean;
  error?: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
  onPreview?: () => void;
  onPublish?: () => void;
  onArchive?: () => void;
}

const newModule = (count: number): CourseFormModule => ({
  id: crypto.randomUUID(),
  uuid: null,
  title: `Module ${count + 1}`,
  description: "",
  difficulty: null,
  estimatedMinutes: null,
  tags: [],
  sourceLibraryModuleUuid: null,
  blocks: [],
});

/**
 * Two-pane Edit-Course layout. The left column shows the course structure
 * (details + module list); the right pane renders either the course-detail
 * sections or the selected module's content editor. All module management
 * lives here (the Create screen has none).
 */
export function CourseEditorLayout({
  form,
  metadata,
  course,
  loading,
  error,
  isSubmitting,
  onSubmit,
  onPreview,
  onPublish,
  onArchive,
}: CourseEditorLayoutProps) {
  const { watch, setValue } = form;
  const modules = watch("modules");
  const estimatedHours = watch("estimatedHours") ?? "";

  const [activeKey, setActiveKey] = useState("details");
  const [isLibraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const setModules = useCallback(
    (next: CourseFormModule[]) => {
      setValue("modules", next, { shouldDirty: true });
    },
    [setValue],
  );

  const handleAddModule = useCallback(() => {
    const created = newModule(modules.length);
    setModules([...modules, created]);
    setActiveKey(created.id);
  }, [modules, setModules]);

  const handleAddFromLibrary = useCallback(
    (selected: SelectedLibraryModule[]) => {
      const added: CourseFormModule[] = selected.map((libraryModule) => ({
        id: crypto.randomUUID(),
        uuid: null,
        title: libraryModule.title,
        description: "",
        // Difficulty/minutes/tags are copied from the source library module
        // by the server on save (alongside content).
        difficulty: null,
        estimatedMinutes: null,
        tags: [],
        sourceLibraryModuleUuid: libraryModule.uuid,
        blocks: [],
      }));
      setModules([...modules, ...added]);
      setLibraryPickerOpen(false);
      if (added.length > 0) {
        setActiveKey(added[0].id);
      }
    },
    [modules, setModules],
  );

  const handleModuleChange = useCallback(
    (updated: CourseFormModule) => {
      setModules(modules.map((m) => (m.id === updated.id ? updated : m)));
    },
    [modules, setModules],
  );

  const handleDeleteModule = useCallback(
    (id: string) => {
      if (modules.length <= 1) {
        return;
      }
      setModules(modules.filter((m) => m.id !== id));
      setActiveKey("details");
    },
    [modules, setModules],
  );

  const activeModule = useMemo(
    () => modules.find((m) => m.id === activeKey),
    [modules, activeKey],
  );
  const activeModuleIndex = useMemo(
    () => modules.findIndex((m) => m.id === activeKey),
    [modules, activeKey],
  );

  const archiveWarning = buildArchiveWarning(
    course?.enrollmentStats?.totalEnrolled ?? 0,
  );

  const actions = (
    <div className="flex gap-2">
      {onPreview && (
        <Button onClick={onPreview} type="button" variant="outline">
          Preview Course
        </Button>
      )}
      {course && course.status !== "published" && onPublish && (
        <Button onClick={onPublish} type="button" variant="outline">
          Publish
        </Button>
      )}
      {course && course.status !== "archived" && onArchive && (
        <Button
          onClick={() => setShowArchiveDialog(true)}
          type="button"
          variant="outline"
        >
          Archive
        </Button>
      )}
      <Button disabled={isSubmitting} onClick={onSubmit} type="button">
        Update Course
      </Button>
    </div>
  );

  return (
    <MainPageWrapper
      actions={actions}
      error={error}
      loading={loading}
      subtitle="Edit course details, modules, and content."
      title="Edit Course"
    >
      {course && <CourseEnrollmentStats stats={course.enrollmentStats} />}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="lg:sticky lg:top-6 space-y-3">
            <CourseStructureNav
              activeKey={activeKey}
              estimatedHours={estimatedHours}
              modules={modules}
              onAddModule={handleAddModule}
              onReorder={setModules}
              onSelect={setActiveKey}
            />
            <Button
              className="w-full"
              onClick={() => setLibraryPickerOpen(true)}
              type="button"
              variant="outline"
            >
              Add from Library
            </Button>
          </div>
        </aside>

        <main className="lg:col-span-8 xl:col-span-9">
          {activeKey === "details" || !activeModule ? (
            <CourseDetailsFields
              course={course}
              form={form}
              metadata={metadata}
            />
          ) : (
            <CourseModulePane
              canDelete={modules.length > 1}
              index={activeModuleIndex}
              module={activeModule}
              onChange={handleModuleChange}
              onDelete={() => handleDeleteModule(activeModule.id)}
            />
          )}
        </main>
      </div>

      <AddFromLibraryModal
        isOpen={isLibraryPickerOpen}
        onClose={() => setLibraryPickerOpen(false)}
        onConfirm={handleAddFromLibrary}
      />

      {course && onArchive && (
        <ConfirmationDialog
          cancelMessage="Cancel"
          confirmMessage="Archive"
          dialogInstructionFinalQuestion="Are you sure you want to archive this course?"
          dialogInstructionItems={archiveWarning.items}
          dialogInstructionTitle={archiveWarning.title}
          dialogTitleContent={`Archive "${course.title}"?`}
          isOpen={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={() => {
            setShowArchiveDialog(false);
            onArchive();
          }}
        />
      )}
    </MainPageWrapper>
  );
}
